import React, { useMemo, useState, useCallback } from 'react';
import { Typography, Box, Paper } from '@mui/material';

export interface OhlcBar {
  datetime: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export interface ForecastPoint {
  step: number;
  projectedClose: number;
  bandLow: number;
  bandHigh: number;
}

function isValidBar(b: OhlcBar): boolean {
  return (
    Number.isFinite(b.open) &&
    Number.isFinite(b.high) &&
    Number.isFinite(b.low) &&
    Number.isFinite(b.close) &&
    b.high >= b.low
  );
}

function fmtShortDate(dt: string): string {
  const t = dt.trim();
  if (t.length >= 10) {
    return t.slice(5, 10).replace('-', '/');
  }
  return t.slice(0, 8);
}

interface Props {
  bars: OhlcBar[];
  maxBars?: number;
  height?: number;
  forecastPoints?: ForecastPoint[];
}

const BreezeCandlestickChart: React.FC<Props> = ({ bars, maxBars = 72, height = 380, forecastPoints }) => {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const slice = useMemo(() => {
    const v = bars.filter(isValidBar);
    return v.length <= maxBars ? v : v.slice(-maxBars);
  }, [bars, maxBars]);

  const layout = useMemo(() => {
    const w0 = 880;
    const h0 = height;
    const ml = 56;
    const mr = forecastPoints && forecastPoints.length > 0 ? 12 : 8;
    const mt = 32;
    const mb = 44;
    const pw = w0 - ml - mr;
    const ph = h0 - mt - mb;
    const nFut = forecastPoints?.length ?? 0;
    const nHist = slice.length;
    const totalSlots = nHist + nFut;
    if (totalSlots === 0) {
      return null;
    }

    let min = Infinity;
    let max = -Infinity;
    slice.forEach((b) => {
      min = Math.min(min, b.low);
      max = Math.max(max, b.high);
    });
    if (forecastPoints && forecastPoints.length > 0) {
      forecastPoints.forEach((f) => {
        min = Math.min(min, f.bandLow, f.projectedClose);
        max = Math.max(max, f.bandHigh, f.projectedClose);
      });
    }
    const pad = (max - min) * 0.08 || Math.abs(max) * 0.02 || 1;
    min -= pad;
    max += pad;

    const yPx = (p: number) => mt + ph - ((p - min) / (max - min)) * ph;
    const gap = pw / totalSlots;

    return { w0, h0, ml, mr, mt, mb, pw, ph, min, max, gap, yPx, nHist, nFut };
  }, [slice, height, forecastPoints]);

  const onSvgLeave = useCallback(() => setHoverIdx(null), []);

  if (slice.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No OHLC points to plot.
      </Typography>
    );
  }

  if (!layout) {
    return null;
  }

  const { w0, h0, ml, mt, ph, gap, yPx, nHist } = layout;

  const gridLines: React.ReactNode[] = [];
  const { min, max } = layout;
  for (let g = 0; g <= 4; g++) {
    const gy = mt + (ph * g) / 4;
    const gp = max - ((max - min) * g) / 4;
    gridLines.push(
      <line key={`h${g}`} x1={ml} y1={gy} x2={ml + layout.pw} y2={gy} stroke="#e0e0e0" strokeWidth={1} strokeDasharray="4 4" />
    );
    gridLines.push(
      <text key={`yl${g}`} x={ml - 6} y={gy + 4} fontSize={10} fill="#666" textAnchor="end">
        {gp.toFixed(2)}
      </text>
    );
  }

  const labelStep = Math.max(1, Math.floor(nHist / 8));
  const xLabels: React.ReactNode[] = [];
  for (let i = 0; i < nHist; i += labelStep) {
    const x = ml + i * gap + gap / 2;
    xLabels.push(
      <text key={`xl-${i}`} x={x} y={h0 - 12} fontSize={9} fill="#555" textAnchor="middle" transform={`rotate(-38 ${x} ${h0 - 12})`}>
        {fmtShortDate(slice[i].datetime)}
      </text>
    );
  }

  const candles = slice.map((b, i) => {
    const xCenter = ml + i * gap + gap / 2;
    const up = b.close >= b.open;
    const col = up ? '#1b5e20' : '#b71c1c';
    const bodyTop = yPx(Math.max(b.open, b.close));
    const bodyBot = yPx(Math.min(b.open, b.close));
    const bh = Math.max(1.2, bodyBot - bodyTop);
    const wickTop = yPx(b.high);
    const wickBot = yPx(b.low);
    const candleW = Math.max(2, gap * 0.62);
    const xBody = xCenter - candleW / 2;
    const hi = hoverIdx === i;
    return (
      <g
        key={`c-${b.datetime}-${i}`}
        style={{ cursor: 'crosshair' }}
        onMouseEnter={() => setHoverIdx(i)}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <line x1={xCenter} y1={wickTop} x2={xCenter} y2={wickBot} stroke={col} strokeWidth={hi ? 2.2 : 1.3} />
        <rect
          x={xBody}
          y={bodyTop}
          width={candleW}
          height={bh}
          fill={up ? col : '#fff'}
          fillOpacity={up ? 0.92 : 1}
          stroke={col}
          strokeWidth={hi ? 2 : 1.2}
        />
      </g>
    );
  });

  let forecastLayer: React.ReactNode = null;
  if (forecastPoints && forecastPoints.length > 0 && nHist > 0) {
    const lastIdx = nHist - 1;
    const xLast = ml + lastIdx * gap + gap / 2;
    const yLast = yPx(slice[lastIdx].close);
    const pathParts: string[] = [`M ${xLast} ${yLast}`];
    forecastPoints.forEach((f, j) => {
      const slot = nHist + j;
      const x = ml + slot * gap + gap / 2;
      const y = yPx(f.projectedClose);
      pathParts.push(`L ${x} ${y}`);
    });
    const d = pathParts.join(' ');
    forecastLayer = (
      <>
        <path d={d} fill="none" stroke="#1565c0" strokeWidth={1.8} strokeDasharray="6 4" opacity={0.95} />
        {forecastPoints.map((f, j) => {
          const slot = nHist + j;
          const x = ml + slot * gap + gap / 2;
          const y = yPx(f.projectedClose);
          const yHi = yPx(f.bandHigh);
          const yLo = yPx(f.bandLow);
          const dotR = Math.max(3, gap * 0.18);
          return (
            <g key={`fc-${f.step}`}>
              <line x1={x} y1={yHi} x2={x} y2={yLo} stroke="#42a5f5" strokeWidth={2} strokeOpacity={0.65} />
              <circle cx={x} cy={y} r={dotR} fill="#0d47a1" stroke="#fff" strokeWidth={1} />
              <text x={x} y={h0 - 10} fontSize={9} fill="#1565c0" textAnchor="middle">
                +{f.step}
              </text>
            </g>
          );
        })}
      </>
    );
  }

  const tipBar = hoverIdx != null ? slice[hoverIdx] : null;

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <svg
          viewBox={`0 0 ${w0} ${h0}`}
          width="100%"
          height={h0}
          role="img"
          aria-label="Candlestick chart"
          preserveAspectRatio="xMidYMid meet"
          onMouseLeave={onSvgLeave}
        >
          <defs>
            <linearGradient id="bgGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fafafa" />
              <stop offset="100%" stopColor="#eef2f6" />
            </linearGradient>
          </defs>
          <rect x={0} y={0} width={w0} height={h0} fill="url(#bgGrad)" />
          <text x={ml} y={20} fontSize={12} fill="#333" fontWeight={600}>
            {slice.length} bars
            {forecastPoints && forecastPoints.length > 0 ? ` + ${forecastPoints.length} forward steps (illustrative)` : ''}
          </text>
          <text x={w0 - 12} y={20} fontSize={10} fill="#666" textAnchor="end">
            {min.toFixed(2)} – {max.toFixed(2)}
          </text>
          {gridLines}
          {candles}
          {forecastLayer}
          {xLabels}
        </svg>
      </Box>
      {tipBar && (
        <Paper variant="outlined" sx={{ mt: 1, p: 1.25, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {tipBar.datetime}
          </Typography>
          <Typography variant="body2">
            O <strong>{tipBar.open.toFixed(2)}</strong> H <strong>{tipBar.high.toFixed(2)}</strong> L{' '}
            <strong>{tipBar.low.toFixed(2)}</strong> C <strong>{tipBar.close.toFixed(2)}</strong>
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default BreezeCandlestickChart;
